<template>
  <transition name="c-modal-transition" mode="out-in" @enter="onEnter" @before-leave="onBeforeLeave">
    <div class="c-modal-wrapper" v-if="modals.length">
      <base-modal :key="modal.identifier" v-for="modal in modals" v-bind="modal.options" v-on="modal.events">
        <component :is="modal.component.is" :identifier="modal.identifier" v-bind="modal.component.data" v-on="modal.component.events"/>

        <div slot="modal-footer" slot-scope="{ ok, cancel, hide, close }" v-if="modal.actions">
          <base-button :key="index" v-for="(action, index) in modal.actions" v-bind="action.options" :show-loader="action.isLoading(modal.state)" :disabled="action.isDisabled(modal.state)" @click="action.onClick({ ok, cancel, hide, close })" />
        </div>
      </base-modal>
    </div>
  </transition>
</template>

<script>
/**
 * How to create a modal
 * this.$store.dispatch('showModal', {
 *  component: {
 *    is: PaymentJobPaymentHistoryDetails,
 *    data: {
 *      paymentJobReference: '123'
 *    },
 *    events: {
 *      update: () => console.log('On Update - this.$emit('update')')
 *    }
 *  },
 *  options: {
 *    title: 'Modal title'
 *  },
 *  events: {
 *    ok: () => console.log('On ok event'),
 *    cancel: () => console.log('On cancel event'),
 *    close: () => console.log('On close event')
 *  },
 *  actions: [{
 *    onClick: ({ ok, cancel, hide }) => ok(),
 *    isDisabled: ({ isValid, isLoading }) => !isValid || isLoading,
 *    isLoading: ({ isLoading }) => isLoading,
 *    options: {
 *      title: 'Close',
 *      variant: 'primary'
 *    }
 *  }, {
 *    onClick: ({ cancel }) => cancel(),
 *    isDisabled: ({ isLoading }) => isLoading,
 *    options: {
 *      title: 'Cancel',
 *      variant: 'outline-primary'
 *    }
 *  }]
 * })
 *
 * Component:
 *  - is: which component to show, can be a string if the component is a base component, or the compoment it self
 *  - data: this object will bind as v-bind (props) to the component
 *  - events: this object will bind as v-on (handlers) to the component
 *
 * Options: will bind to the <base-modal> as v-bind
 * - title: modal title
 * - canClose: enable or disable cancel, option to specify which method is allowed: ['escape', 'outside', 'x', 'close']
 *
 * Default options:
 * - canClose: ['escape', 'outside', 'x', 'close']
 * - isScrollable: true
 * - size: 'xxl'
 *
 * State: Can be used to store the state of the modal
 * - isValid: If the modal contains an form, this can holds the state of the form
 * - isLoading: If the modal is doing hard work, you can show an loader on the footer buttons
 *
 * Default state:
 * - isValid: true
 * - isLoading: false
 *
 * Events: will bind to the <base-modal> as v-on, all are optional
 * - ok: On ok event
 * - cancel: On cancel event
 * - close: On close event
 * - ...
 *
 * Default events:
 * - ok: (event) => this.$eventHub.emit('ok', event), - Cancelable
 * - cancel: (event) => this.$eventHub.emit('cancel', event) - Cancelable
 * - hidden: () => commit('removeModal', identifier) } // This will remove the modal from the store after the modal is hidden
 *
 * Actions: array of buttons which to render
 * - onClick: which modal action would you perform, params: Object with ok, cancel, and hide functions, you can deconstruct this object ( { cancel })
 * - isDisabled: disable the button based on the modal state
 * - isLoading: show the spinner based on the modal state
 * - options: this object will bind as v-bind (props) to the component
 *
 * Default action
 * - isDisabled: () => false
 * - isLoading: () => false
 */

import { mapState } from 'vuex'

import BaseModal from '@/components/Modals/BaseModal.vue'
import MessageModal from '@/components/Modals/Components/MessageModal.vue'

export default {
  name: 'modals',

  components: { BaseModal, MessageModal },

  computed: mapState({
    modals: state => state.modals
  }),

  mounted () {
    this.$watch(() => this.modals.length, length => {
      length > 0 ? document.body.classList.add('is-noscroll') : document.body.classList.remove('is-noscroll')
    }, { immediate: true })
  },

  methods: {
    onEnter () {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth

      document.body.style.paddingRight = `${scrollbarWidth}px`
      document.querySelector('.c-modal-wrapper').style.paddingRight = `${scrollbarWidth}px`
    },

    onBeforeLeave () {
      document.body.style.paddingRight = null
      document.querySelector('.c-modal-wrapper').style.paddingRight = null
    }
  }
}
</script>
